// Disponibilidade declarada de um vínculo FLEXÍVEL (`PJ`/`CooperadoCPESQ`) na tela "Meu cadastro" (REQ-PPL-006, 007; Parte 6.2/6.5):
// a própria pessoa declara as janelas em que aceita atuar — `Weekly` (faixas por dia da semana) ou `Dated` (uma data com
// início e fim) —, sem razão no corpo (fixa `PPL_SELF_AVAILABILITY` no servidor) e com o histórico de quem declarou. O tipo da
// declaração é imutável. Vínculo flexível nunca tem escala de trabalho (regra 9 da Parte 0.3): este painel é só de disponibilidade.
//
// Edição em qualquer sessão (OPEN-096): o `PUT` exige `If-Match`, então o diálogo de edição LÊ a declaração
// (`GET /me/person/links/{linkId}/availability/{declarationId}`) depois de abrir e usa o `ETag` dessa leitura — nunca o da lista.
import { zodResolver } from '@hookform/resolvers/zod';
import type { NamespaceKey } from '@giyus/i18n';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  NativeRadio,
  Skeleton,
} from '@giyus/ui';
import { useState } from 'react';
import {
  type Control,
  type FieldErrors,
  type UseFormRegister,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { useMe } from '../../../lib/auth/authContext';
import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import { Field } from '../../authz/components/Fields';
import {
  AVAILABILITY_KINDS,
  WEEK_DAYS,
  type Availability,
  type WeekDay,
  useCreateMyAvailability,
  useMyAvailability,
  useMyAvailabilityDeclaration,
  useUpdateMyAvailability,
} from '../api/queries';
import { useCivilDate } from '../lib/civilDateDisplay';
import {
  type AvailabilityFormValues,
  availabilitySchema,
  availabilityToForm,
  emptyAvailabilityForm,
  shortTime,
  toAvailabilityInput,
  weeklyWindows,
} from '../lib/selfService';

type Editing =
  | { readonly mode: 'new' }
  | { readonly mode: 'edit'; readonly item: Availability; readonly etag: string };

/** O que o usuário pediu: declarar uma nova ou editar uma existente (a leitura com o ETag vem depois de abrir o diálogo). */
type EditRequest =
  { readonly mode: 'new' } | { readonly mode: 'edit'; readonly item: Availability };

export function MyAvailabilityPanel({ linkId }: { readonly linkId: string }) {
  const { t } = useTranslation('people');
  const { t: tc } = useTranslation('common');
  const dates = useDateFormatter();
  const civil = useCivilDate();
  const me = useMe();
  const list = useMyAvailability(linkId);
  const [editing, setEditing] = useState<EditRequest | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const create = useCreateMyAvailability(linkId);
  const update = useUpdateMyAvailability(linkId);

  const close = (): void => {
    create.reset();
    update.reset();
    setEditing(null);
  };

  return (
    <div className="flex flex-col gap-3" data-testid={`my-availability-${linkId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-body font-semibold">{t('me.availability.title')}</h4>
          <p className="text-body text-muted-foreground">{t('me.availability.description')}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          data-testid={`my-availability-new-${linkId}`}
          onClick={() => {
            create.reset();
            setMessage(null);
            setEditing({ mode: 'new' });
          }}
        >
          {t('me.availability.declare')}
        </Button>
      </div>
      {message && (
        <p role="status" className="text-body" data-testid="my-availability-message">
          {message}
        </p>
      )}
      {list.isError ? (
        <ProblemAlert
          error={list.error}
          onRetry={() => {
            void list.refetch();
          }}
        />
      ) : list.isLoading || !list.data ? (
        <Skeleton className="h-16 w-full" />
      ) : list.data.length === 0 ? (
        <p className="text-body text-muted-foreground" data-testid="my-availability-empty">
          {t('me.availability.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {list.data.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded border p-2"
              data-testid={`my-availability-item-${item.id}`}
            >
              <div className="flex flex-col gap-1 text-body">
                <span>
                  <Badge variant="outline">
                    {t(`common.availabilityKind.${item.kind}`, { defaultValue: item.kind })}
                  </Badge>
                </span>
                {item.kind === 'Dated' && item.date ? (
                  <span>
                    {civil(item.date)}
                    {item.startTime && item.endTime
                      ? ` · ${shortTime(item.startTime)}–${shortTime(item.endTime)}`
                      : ''}
                  </span>
                ) : (
                  <ul className="flex flex-col gap-0.5">
                    {weeklyWindows(item).map(({ day, windows }) => (
                      <li key={day}>
                        <span className="font-medium">{t(`common.weekday.${day}`)}</span>
                        {': '}
                        {windows.join(', ')}
                      </li>
                    ))}
                  </ul>
                )}
                <span className="text-caption text-muted-foreground">
                  {t('me.availability.declared', {
                    when: dates.dateTime(item.declaredAt),
                    by:
                      item.declaredBy === me.user.id
                        ? t('me.availability.byYou')
                        : t('me.availability.byCoordination'),
                  })}
                </span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid={`my-availability-edit-${item.id}`}
                onClick={() => {
                  update.reset();
                  setMessage(null);
                  setEditing({ mode: 'edit', item });
                }}
              >
                {t('me.availability.edit')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            close();
          }
        }}
      >
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
          closeLabel={tc('a11y.closeDialog')}
          data-testid="my-availability-dialog"
        >
          {editing && (
            <EditSession
              key={editing.mode === 'edit' ? editing.item.id : 'new'}
              linkId={linkId}
              request={editing}
              pending={create.isPending || update.isPending}
              error={create.error ?? update.error}
              onCancel={close}
              onSubmit={async (editingNow, values) => {
                try {
                  if (editingNow.mode === 'edit') {
                    await update.mutateAsync({
                      declarationId: editingNow.item.id,
                      etag: editingNow.etag,
                      input: toAvailabilityInput(values, editingNow.item.siteId),
                    });
                  } else {
                    await create.mutateAsync(toAvailabilityInput(values));
                  }
                  setMessage(t('me.availability.saved'));
                  close();
                } catch {
                  // O erro fica no ProblemAlert do diálogo.
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Declarar: formulário vazio. Editar: espera o GET da declaração (feito depois de abrir) e só então monta o formulário com os campos e o
 * `ETag` dessa mesma leitura, congelada ao montar (OPEN-096) — nunca a linha da lista (sem `ETag`) nem um cache de outra abertura.
 */
function EditSession({
  linkId,
  request,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  readonly linkId: string;
  readonly request: EditRequest;
  readonly pending: boolean;
  readonly error: unknown;
  readonly onCancel: () => void;
  readonly onSubmit: (editing: Editing, values: AvailabilityFormValues) => Promise<void>;
}) {
  const { t } = useTranslation('people');
  const { t: tc } = useTranslation('common');
  const declarationId = request.mode === 'edit' ? request.item.id : '';
  const current = useMyAvailabilityDeclaration(linkId, declarationId);
  if (request.mode === 'new') {
    return (
      <AvailabilityForm
        editing={{ mode: 'new' }}
        pending={pending}
        error={error}
        onCancel={onCancel}
        onSubmit={(values) => onSubmit({ mode: 'new' }, values)}
      />
    );
  }
  if (current.isError || !current.data || !current.isFetchedAfterMount) {
    return (
      <div className="flex flex-col gap-4" data-testid="my-availability-loading">
        <DialogHeader>
          <DialogTitle>{t('me.availability.editTitle')}</DialogTitle>
          <DialogDescription>{t('me.availability.formDescription')}</DialogDescription>
        </DialogHeader>
        {current.isError ? (
          <ProblemAlert
            error={current.error}
            onRetry={() => {
              void current.refetch();
            }}
          />
        ) : (
          <Skeleton className="h-24 w-full" />
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {tc('actions.cancel')}
          </Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <FrozenEdit
      read={current.data}
      pending={pending}
      error={error}
      onCancel={onCancel}
      onSubmit={onSubmit}
    />
  );
}

/** Congela a leitura: uma releitura em segundo plano não troca o ETag por baixo de campos que o usuário já viu. */
function FrozenEdit({
  read,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  readonly read: { readonly data: Availability; readonly etag: string | null };
  readonly pending: boolean;
  readonly error: unknown;
  readonly onCancel: () => void;
  readonly onSubmit: (editing: Editing, values: AvailabilityFormValues) => Promise<void>;
}) {
  const [snapshot] = useState(read);
  const editing: Editing | null = snapshot.etag
    ? { mode: 'edit', item: snapshot.data, etag: snapshot.etag }
    : null;
  if (!editing) {
    return <ProblemAlert error={new Error('The availability version (ETag) is unknown.')} />;
  }
  return (
    <AvailabilityForm
      editing={editing}
      pending={pending}
      error={error}
      onCancel={onCancel}
      onSubmit={(values) => onSubmit(editing, values)}
    />
  );
}

function AvailabilityForm({
  editing,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  readonly editing: Editing;
  readonly pending: boolean;
  readonly error: unknown;
  readonly onCancel: () => void;
  readonly onSubmit: (values: AvailabilityFormValues) => Promise<void>;
}) {
  const { t } = useTranslation('people');
  const { t: tc } = useTranslation('common');
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AvailabilityFormValues>({
    resolver: zodResolver(availabilitySchema),
    defaultValues:
      editing.mode === 'edit' ? availabilityToForm(editing.item) : emptyAvailabilityForm(),
  });
  const kind = useWatch({ control, name: 'kind' });
  const translate = (text: string | undefined): string | undefined =>
    text ? t(text as NamespaceKey<'people'>) : undefined;

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      data-testid="my-availability-form"
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
    >
      <DialogHeader>
        <DialogTitle>
          {editing.mode === 'edit' ? t('me.availability.editTitle') : t('me.availability.newTitle')}
        </DialogTitle>
        <DialogDescription>{t('me.availability.formDescription')}</DialogDescription>
      </DialogHeader>
      {editing.mode === 'new' ? (
        <fieldset className="flex flex-wrap gap-4">
          <legend className="mb-1 text-body font-medium">{t('me.availability.kind')}</legend>
          {AVAILABILITY_KINDS.map((value) => (
            <label key={value} className="flex min-h-8 items-center gap-2 text-body">
              <NativeRadio value={value} {...register('kind')} />
              {t(`common.availabilityKind.${value}`)}
            </label>
          ))}
        </fieldset>
      ) : (
        <p className="text-body text-muted-foreground">
          {t('me.availability.kindFixed', {
            kind: t(`common.availabilityKind.${kind}`),
          })}
        </p>
      )}

      {kind === 'Weekly' ? (
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-body font-medium">{t('me.availability.weekly')}</legend>
          {WEEK_DAYS.map((day) => (
            <DayWindows key={day} day={day} control={control} register={register} errors={errors} />
          ))}
          {errors.weekly?.message && (
            <p
              role="alert"
              className="text-caption text-destructive"
              data-testid="my-availability-weekly-error"
            >
              {translate(errors.weekly.message)}
            </p>
          )}
        </fieldset>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            id="avail-date"
            label={t('me.availability.date')}
            error={translate(errors.date?.message)}
          >
            <Input id="avail-date" type="date" {...register('date')} />
          </Field>
          <Field
            id="avail-start"
            label={t('me.availability.start')}
            error={translate(errors.startTime?.message)}
          >
            <Input id="avail-start" type="time" {...register('startTime')} />
          </Field>
          <Field
            id="avail-end"
            label={t('me.availability.end')}
            error={translate(errors.endTime?.message)}
          >
            <Input id="avail-end" type="time" {...register('endTime')} />
          </Field>
        </div>
      )}

      {error !== null && error !== undefined && <ProblemAlert error={error} />}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {tc('actions.cancel')}
        </Button>
        <Button type="submit" disabled={pending} data-testid="my-availability-save">
          {tc('actions.save')}
        </Button>
      </DialogFooter>
    </form>
  );
}

function DayWindows({
  day,
  control,
  register,
  errors,
}: {
  readonly day: WeekDay;
  readonly control: Control<AvailabilityFormValues>;
  readonly register: UseFormRegister<AvailabilityFormValues>;
  readonly errors: FieldErrors<AvailabilityFormValues>;
}) {
  const { t } = useTranslation('people');
  const { fields, append, remove } = useFieldArray({ control, name: `weekly.${day}` });
  const translate = (text: string | undefined): string | undefined =>
    text ? t(text as NamespaceKey<'people'>) : undefined;
  const dayErrors = errors.weekly?.[day];
  const rootError = translate(dayErrors?.root?.message);
  return (
    <div className="flex flex-col gap-2" data-testid={`avail-day-${day}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-body font-medium">{t(`common.weekday.${day}`)}</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            append({ start: '09:00', end: '13:00' });
          }}
        >
          {t('me.availability.addWindow', { day: t(`common.weekday.${day}`) })}
        </Button>
      </div>
      {fields.map((field, index) => {
        const rowErrors = dayErrors?.[index];
        const startMessage = translate(rowErrors?.start?.message);
        const endMessage = translate(rowErrors?.end?.message);
        return (
          <div key={field.id} className="flex flex-wrap items-start gap-2">
            <div className="flex flex-col gap-1">
              <Input
                type="time"
                className="w-32"
                aria-label={t('me.availability.windowStart', {
                  day: t(`common.weekday.${day}`),
                  index: index + 1,
                })}
                {...register(`weekly.${day}.${index}.start`)}
              />
              {startMessage && (
                <p role="alert" className="text-caption text-destructive">
                  {startMessage}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Input
                type="time"
                className="w-32"
                aria-label={t('me.availability.windowEnd', {
                  day: t(`common.weekday.${day}`),
                  index: index + 1,
                })}
                {...register(`weekly.${day}.${index}.end`)}
              />
              {endMessage && (
                <p role="alert" className="text-caption text-destructive">
                  {endMessage}
                </p>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                remove(index);
              }}
            >
              {t('me.availability.removeWindow', {
                day: t(`common.weekday.${day}`),
                index: index + 1,
              })}
            </Button>
          </div>
        );
      })}
      {rootError && (
        <p role="alert" className="text-caption text-destructive">
          {rootError}
        </p>
      )}
    </div>
  );
}
